'use client';

import {
    GRS_REFINE_QUERY_PARAM,
    LW_REFINE_QUERY_PARAM,
} from '../SearchResultsFacets/constants';

import { getSingleNonDefaultRefinementLabel } from '@/components/SearchResultsFacets/utils';
import { SEARCH_SERVICE_PROVIDERS } from '@/constants/grs';
import { getKeyValue } from '@/services/content/data';
import { useSearchContext } from '@/services/search/SearchProvider';
import {
    FIRST_ITEM_INDEX_TOKEN,
    LAST_ITEM_INDEX_TOKEN,
    PRODUCT_COUNT_LABEL_KEY,
    TOTAL_ITEMS_TOKEN,
} from '@/src/containers/main/constants';
import {
    getFirstItemIndex,
    getLastItemIndex,
} from '@/src/containers/main/utils';
import type {
    CategoryLandingPage,
    GRSFacetConfigProperties,
    SearchRuleEntry,
} from '@/types/contentStack';
import { KeyValueToken } from '@/types/generics';
import { Skeleton } from '@nxt-costco-com/forge-components';
import {
    type ContentStackEntryDataProps,
    useQueryParams,
} from '@nxt-costco-com/forge-digital-components';

import {
    CORRECTED_TOKEN,
    DIRECT_MATCH_TEXT_KEY,
    FILTER_LABEL_TOKEN,
    NO_RESULTS_TEXT_KEY,
    RESULT_COUNT_TOKEN,
    SEARCH_TOKEN,
    SEMANTIC_MATCH_TEXT_KEY,
    SINGLE_FILTER_TEXT_KEY,
} from './constants';
import { agnosticReplaceTokens, isSearchRuleEntry } from './utils';

import { StyledHeading, StyledHeadingH2 } from './styles';

const COMPONENT_NAME = 'searchResults-heading';

export function SearchResultsHeader({
    keywordEntry,
    pageConfig,
    enableLivePreview,
    livePreviewKeyword,
    resultsPerPage,
    grsFacetConfig,
    facetConfig,
}: {
    keywordEntry?: SearchRuleEntry | CategoryLandingPage;
    pageConfig: ContentStackEntryDataProps;
    enableLivePreview?: boolean;
    livePreviewKeyword?: string;
    resultsPerPage: number;
    grsFacetConfig?: ContentStackEntryDataProps<GRSFacetConfigProperties>;
    facetConfig?: ContentStackEntryDataProps;
}) {
    const { queryParams: searchParamsMap } = useQueryParams();
    const effectiveSearchParamsMap =
        enableLivePreview && livePreviewKeyword
            ? new URLSearchParams({ keyword: livePreviewKeyword })
            : searchParamsMap;

    const searchParams = Object.fromEntries(effectiveSearchParamsMap.entries());
    const { searchResult, isSearchResultsLoading, searchService } =
        useSearchContext();

    if (isSearchResultsLoading) {
        return (
            <StyledHeading>
                {/* @ts-expect-error TODO: export this or let text as variant */}
                <Skeleton variant="textHeader" />
            </StyledHeading>
        );
    }

    if (!searchResult) {
        return null;
    }

    const { pagination, metrics } = searchResult;

    if (pagination?.totalDocs === 0) {
        return (
            <StyledHeading data-testid={COMPONENT_NAME}>
                {getKeyValue(NO_RESULTS_TEXT_KEY, pageConfig)}
            </StyledHeading>
        );
    }

    if (Array.isArray(searchParams.keyword)) {
        searchParams.keyword = searchParams.keyword[0];
    }

    const firstDisplayedItemIndex = getFirstItemIndex(
        searchResult.pagination?.currentPage,
        resultsPerPage,
    );

    const lastDisplayedItemIndex = getLastItemIndex(
        searchResult.pagination?.currentPage,
        resultsPerPage,
        searchResult.pagination?.totalDocs,
    );

    // TODO: Replace with agnosticReplaceTokens reusable function from utils.
    function replaceTokens(template: string | undefined) {
        if (!template) {
            return '';
        }

        return template
            .replace(SEARCH_TOKEN, searchParams.keyword as string)
            .replace(CORRECTED_TOKEN, metrics.correction!)
            .replace(RESULT_COUNT_TOKEN, pagination?.totalDocs.toLocaleString())
            .replace(
                FIRST_ITEM_INDEX_TOKEN,
                firstDisplayedItemIndex.toLocaleString(),
            )
            .replace(
                LAST_ITEM_INDEX_TOKEN,
                lastDisplayedItemIndex.toLocaleString(),
            )
            .replace(TOTAL_ITEMS_TOKEN, pagination?.totalDocs.toString());
    }

    // If we have results (checked above), but no keyword, show the text without the keyword (used on CLP)
    if (!searchParams.keyword) {
        return (
            <StyledHeadingH2 data-testid={COMPONENT_NAME}>
                {replaceTokens(
                    getKeyValue(PRODUCT_COUNT_LABEL_KEY, pageConfig),
                )}
            </StyledHeadingH2>
        );
    }

    // There are a bunch of special cases for showing the header text based on filters applied
    const refinementKey =
        searchService === SEARCH_SERVICE_PROVIDERS.GRS
            ? GRS_REFINE_QUERY_PARAM
            : LW_REFINE_QUERY_PARAM;
    let refine = searchParams[refinementKey];
    // Added for edge case scenarios where there will be multiple refine params in url.
    if (Array.isArray(refine)) {
        refine = refine[0] as string;
    }

    // 1. If we found results for the search term and have a search page title override
    if (isSearchRuleEntry(keywordEntry) && keywordEntry?.search_page_title) {
        return (
            <StyledHeading data-testid={COMPONENT_NAME}>
                {keywordEntry.search_page_title}
            </StyledHeading>
        );
    }

    let existingNonDefaultRefinementLabel = '';

    if (refine) {
        existingNonDefaultRefinementLabel = getSingleNonDefaultRefinementLabel({
            refine,
            searchService,
            grsFacetConfig,
            facetConfig,
        });
    }
    // 2. If only one filter is applied, show the filter label and the search term
    if (existingNonDefaultRefinementLabel) {
        return (
            <StyledHeading data-testid={COMPONENT_NAME}>
                {replaceSingleFilterToken(
                    replaceTokens(
                        getKeyValue(SINGLE_FILTER_TEXT_KEY, pageConfig),
                    ),
                )}
            </StyledHeading>
        );
    }

    function replaceSingleFilterToken(template: string | undefined) {
        if (!template) {
            return '';
        }

        return template.replace(
            FILTER_LABEL_TOKEN,
            existingNonDefaultRefinementLabel,
        );
    }

    // 5. If we found results for the search term, display it with the number of results
    const resultsTerm = metrics?.correction
        ? metrics.correction
        : (searchParams.keyword as string);

    const semanticMatchResultsText = getKeyValue(
        SEMANTIC_MATCH_TEXT_KEY,
        pageConfig,
    );

    const directMatchResultsText = getKeyValue(
        DIRECT_MATCH_TEXT_KEY,
        pageConfig,
    );

    const replacementTokens: KeyValueToken[] = [
        { [`${RESULT_COUNT_TOKEN}`]: pagination?.totalDocs?.toLocaleString() },
        {
            [`${FIRST_ITEM_INDEX_TOKEN}`]:
                firstDisplayedItemIndex?.toLocaleString(),
        },
        {
            [`${LAST_ITEM_INDEX_TOKEN}`]:
                lastDisplayedItemIndex?.toLocaleString(),
        },
    ];

    const resultsCountHeadingLabel = metrics?.semantic
        ? agnosticReplaceTokens(semanticMatchResultsText, replacementTokens)
        : agnosticReplaceTokens(directMatchResultsText, [
              { [`${SEARCH_TOKEN}`]: resultsTerm },
              ...replacementTokens,
          ]);

    const ResultsCountStyledHeading = () => {
        return metrics?.correction || metrics?.semantic ? (
            // H1 is used for corrected and semantic results headings, so avoid SEO ding from two H1s.
            <StyledHeadingH2 data-testid={COMPONENT_NAME}>
                {resultsCountHeadingLabel}
            </StyledHeadingH2>
        ) : (
            <StyledHeading data-testid={COMPONENT_NAME}>
                {resultsCountHeadingLabel}
            </StyledHeading>
        );
    };

    return <ResultsCountStyledHeading />;
}
